import { useEffect, useState } from 'react';
import { Document, documentsAPI } from '../../lib/api';
import type { DocumentHistoryEntry, DocumentVersionEntry } from '../../lib/api/documents';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { toast } from 'sonner';

/** Everything DocumentDetailModal reads about the open document, plus the actions that refresh it. */
export interface DocumentDetailData {
  history: DocumentHistoryEntry[];
  historyLoading: boolean;
  historyError: string | null;
  versions: DocumentVersionEntry[];
  versionsLoading: boolean;
  versionsError: string | null;
  /** True once the current viewer has recorded Read & Accepted. */
  hasAccepted: boolean;
  acceptedAt: string | null;
  acceptanceCount: number;
  accepting: boolean;
  /** The issuing customer's acknowledgement wording; '' keeps the standard text. */
  acknowledgementMessage: string;
  /** Whether the current viewer is one of the intended Read & Accept recipients. */
  isRecipient: boolean;
  refreshVersions: (docId: number) => void;
  refreshHistory: (docId: number) => void;
  /** Records the viewer's acceptance of the open document. */
  handleAccept: () => Promise<void>;
}

/**
 * Loads and holds the data behind the document detail modal (CR-5330).
 *
 * While `open` and `document` are both set it fetches the audit history, the
 * version list and the document detail record (the latter carries the
 * per-viewer acceptance state the list serializer does not expose). The three
 * requests fire together, in that order, whenever `open` or `document` changes.
 *
 * @param document - The document being shown, or null when none is selected.
 * @param open - Whether the modal is open; nothing is fetched while closed.
 * @returns The loaded history / versions / acceptance state and the actions that refresh them.
 *
 * @example
 * const { history, versions, handleAccept } = useDocumentDetailData(document, open);
 */
export function useDocumentDetailData(document: Document | null, open: boolean): DocumentDetailData {
  const [history, setHistory] = useState<DocumentHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  // CR-5328: Versions tab state.
  const [versions, setVersions] = useState<DocumentVersionEntry[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState<string | null>(null);
  // CR-5329: per-viewer acceptance state. Populated from the doc-detail
  // fetch (the list serializer doesn't expose has_accepted), then
  // updated locally after a successful accept POST so the banner flips
  // without a full refetch.
  const [hasAccepted, setHasAccepted] = useState(false);
  const [acceptedAt, setAcceptedAt] = useState<string | null>(null);
  const [acceptanceCount, setAcceptanceCount] = useState(0);
  const [accepting, setAccepting] = useState(false);
  // The issuing customer's acknowledgement wording (Admin Setup); '' keeps the standard text.
  const [acknowledgementMessage, setAcknowledgementMessage] = useState('');
  // CR-5329 round 2: per-viewer "am I a recipient?" flag from the BE
  // detail serializer. The banner only renders when this is true so
  // customer/uploader-side users don't get the misleading "you must
  // acknowledge" prompt for docs they themselves uploaded.
  const [isRecipient, setIsRecipient] = useState(false);

  const refreshVersions = (docId: number) => {
    setVersionsLoading(true);
    setVersionsError(null);
    documentsAPI.getDocumentVersions(docId)
      .then(res => setVersions(res.data || []))
      .catch(err => {
        console.error('Error loading document versions:', err);
        setVersionsError('Failed to load version history');
      })
      .finally(() => setVersionsLoading(false));
  };

  const refreshHistory = (docId: number) => {
    setHistoryLoading(true);
    setHistoryError(null);
    documentsAPI.getDocumentHistory(docId)
      .then(res => setHistory(res.data || []))
      .catch(err => {
        console.error('Error loading document history:', err);
        setHistoryError('Failed to load audit history');
        toast.error('Failed to load audit history');
      })
      .finally(() => setHistoryLoading(false));
  };

  // CR-5329: fetch the doc detail (which exposes per-viewer has_accepted,
  // accepted_at, acceptance_count) so the banner / badge can render
  // without piggy-backing on the list-serializer payload that's passed
  // in via props.
  const refreshAcceptanceState = (docId: number) => {
    documentsAPI.getDocument(docId)
      .then(res => {
        setHasAccepted(!!res.data.has_accepted);
        setAcceptedAt(res.data.accepted_at || null);
        setAcceptanceCount(res.data.acceptance_count || 0);
        setIsRecipient(!!res.data.is_acceptance_recipient);
        setAcknowledgementMessage(res.data.acknowledgement_message || '');
      })
      .catch(err => {
        console.error('Error fetching document detail:', err);
      });
  };

  useEffect(() => {
    if (!open || !document) {
      return;
    }
    setAcknowledgementMessage('');  // never show the previous document's wording
    refreshHistory(document.id);
    refreshVersions(document.id);
    refreshAcceptanceState(document.id);
  }, [open, document]);

  const handleAccept = async () => {
    if (!document) return;
    try {
      setAccepting(true);
      const res = await documentsAPI.acceptDocument(document.id);
      setHasAccepted(!!res.data.has_accepted);
      setAcceptedAt(res.data.accepted_at || null);
      setAcceptanceCount(res.data.acceptance_count || 0);
      // Refresh history so the new "Read & Accepted" event lands.
      refreshHistory(document.id);
      toast.success('Document marked as Read & Accepted');
    } catch (err) {
      console.error('Error accepting document:', err);
      toast.error(getApiErrorMessage(err, 'Failed to record acceptance'));
    } finally {
      setAccepting(false);
    }
  };

  return {
    history,
    historyLoading,
    historyError,
    versions,
    versionsLoading,
    versionsError,
    hasAccepted,
    acceptedAt,
    acceptanceCount,
    accepting,
    acknowledgementMessage,
    isRecipient,
    refreshVersions,
    refreshHistory,
    handleAccept,
  };
}
